import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { usePlayerAuth } from '../contexts/PlayerAuthContext';
import { useAuth } from '../contexts/AuthContext';
import { decideTenantAccess, isWithinAccessScope, PLAYER_LOGIN_PATH } from '../lib/tenantAccess';
import AppSplash from './AppSplash';

/**
 * Applies a members-only tenant app's access rule (see `lib/tenantAccess`):
 * inside the app, the events page is public and doubles as the home page, and
 * everything else sends a signed-out visitor to the player login. A club's own
 * site, which sits outside the app prefix, is left public.
 */
const TenantAccessGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { tenant, loading: tenantLoading, stripTenantPrefix, tenantPath } = useTenant();
  const { isLoggedIn, isLoading: playerLoading } = usePlayerAuth();
  const { isAuthenticated, isLoading: userLoading } = useAuth();

  const access = tenant?.access;

  const publicPaths = useMemo(
    () => (access?.publicPaths || []).map((p) => stripTenantPrefix(p)),
    [access?.publicPaths, stripTenantPrefix]
  );

  // A club with its own site keeps it public at "/" — only the app under
  // `scopePrefix` is members-only.
  const inScope = isWithinAccessScope(location.pathname, access?.scopePrefix);

  const decision = decideTenantAccess({
    pathname: stripTenantPrefix(location.pathname),
    requireSignIn: Boolean(access?.requireSignIn),
    inScope,
    homePath: access?.homePath ? stripTenantPrefix(access.homePath) : null,
    publicPaths,
    tenantLoading,
    // Club staff sign in as platform users, not players — either session counts.
    signedIn: isLoggedIn || isAuthenticated,
    authLoading: playerLoading || userLoading,
  });

  if (decision.type === 'allow') return <>{children}</>;
  // Restoring a stored session: hold on the splash rather than flashing the
  // login page at a member who is already signed in.
  if (decision.type === 'wait') return <AppSplash />;

  if (decision.to === PLAYER_LOGIN_PATH) {
    const next = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`${tenantPath(PLAYER_LOGIN_PATH)}?next=${encodeURIComponent(next)}`}
        replace
      />
    );
  }
  return <Navigate to={tenantPath(decision.to)} replace />;
};

export default TenantAccessGate;
